import { AfterViewInit, ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { DataList, DropDownList } from '@core/interfaces';
import { EventService, GridActions, GridService, ListService } from '@core/services';
import { remToPx } from '@util/layout';

import { Filters } from '#types/grid';
import { ItemLookupResponseRow } from '#types/item';

import { AuthService } from '@auth/services';
import { CustomerService } from '@customer/services';
import { FacilityService } from '@facility/services';
import { CONSTANTS } from '@item/constants';
import { ItemService } from '@item/services';

@Component({
  selector: 'z-item-lookup-dialog',
  templateUrl: './item-lookup-dialog.component.html',
  styleUrls: ['./item-lookup-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemLookupDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  customerList$ = this.customerService.authorizedCustomerDropdownList$;
  facilityList$ = this.facilityService.authorizedFacilityDropdownList$;

  customerProductGroupDropdownList$ = new BehaviorSubject<DropDownList>(null);
  customerRateGroupDropdownList$ = new BehaviorSubject<DropDownList>(null);

  productGroupLoading$: Observable<boolean>;
  rateGroupLoading$: Observable<boolean>;

  form: FormGroup;

  custid: string;

  item: string;
  selectedRow: ItemLookupResponseRow;

  matchTypes: Record<string, string> = {};

  container: HTMLDivElement;
  containerParent: HTMLDivElement;
  containerFilters: HTMLDivElement;
  containerResizeObserver: ResizeObserver;

  lookupId = 'item-lookup-dialog';

  gridId = CONSTANTS.GRID_ITEM_LOOKUP_DIALOG;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private store: Store,
    public itemService: ItemService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private gridService: GridService,
    private customerService: CustomerService,
    private facilityService: FacilityService,
    private authService: AuthService,
    private ngZone: NgZone,
    private translocoService: TranslocoService,
    private listService: ListService,
    private eventService: EventService
  ) {}

  ngOnInit() {
    // close dialog if no longer authenticated
    this.authService.authenticated$
      .pipe(
        filter((authenticated) => !authenticated),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.onCancelClick();
      });

    this.productGroupLoading$ = this.eventService.loading(CONSTANTS.LIST_ITEM_CUSTOMER_PRODUCT_GROUP);
    this.rateGroupLoading$ = this.eventService.loading(CONSTANTS.LIST_ITEM_CUSTOMER_RATE_GROUP);

    this.itemService.itemLookupDialogSelectedCustomer$.pipe(takeUntil(this.destroy$)).subscribe((custid) => {
      this.custid = custid;
    });

    this.form = new FormGroup({
      item: new FormControl(),
      productGroup: new FormControl(),
      rateGroup: new FormControl(),
    });

    this.gridService
      .selectedRow$<ItemLookupResponseRow>(this.gridId)
      .pipe(
        filter((x) => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe((selectedRow) => {
        this.selectedRow = selectedRow.data;
        this.item = this.selectedRow.item;

        if (selectedRow.selectionType === 'double') {
          this.onSelectClick();
        }
      });

    this.itemService.customerProductGroupList$.pipe(takeUntil(this.destroy$)).subscribe((list) => {
      this.initializeCustomerProductGroupDropdownList(list);
    });

    this.itemService.customerRateGroupList$.pipe(takeUntil(this.destroy$)).subscribe((list) => {
      this.initializeCustomerRateGroupDropdownList(list);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();

    this.destroyResizeObserver();
  }

  ngAfterViewInit() {
    // initial request to populate rate / product group drop downs
    setTimeout(() => {
      this.itemService.requestCustomerProductGroupList(this.custid);
      this.itemService.requestCustomerRateGroupList(this.custid);
    }, 0);

    this.initResizeObserver();
  }

  initResizeObserver() {
    this.containerResizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        const header: HTMLDivElement = this.containerParent.querySelector('.p-dialog-header');
        const filters: HTMLDivElement = this.container.querySelector('.filters');
        const footer: HTMLDivElement = this.container.querySelector('.p-dialog-footer');
        const paginator: HTMLDivElement = this.container.querySelector('.p-paginator');

        let offset = 0;

        if (header) {
          offset += Math.floor(header.offsetHeight);
        }

        if (filters) {
          offset += Math.floor(filters.offsetHeight);
        }

        if (footer) {
          offset += Math.floor(footer.offsetHeight);
        }

        if (paginator) {
          offset += Math.floor(paginator.offsetHeight);
        }

        this.itemService.itemLookupDialogDimensions$.next({
          container: {
            width: Math.floor(this.containerParent.offsetWidth),
            height: Math.floor(this.containerParent.offsetHeight),
          },
          grid: {
            width: Math.floor(this.containerParent.offsetWidth) - remToPx('2rem'),
            height: Math.floor(this.containerParent.offsetHeight) - offset,
          },
        });
      });
    });

    try {
      this.container = document.querySelector(`.${this.lookupId}`);

      if (this.container) {
        this.containerParent = this.container.closest(`.lookup-dialog`);

        if (this.containerParent) {
          this.containerResizeObserver.observe(this.containerParent);
        }

        this.containerFilters = this.container.querySelector('.filters');

        if (this.containerFilters) {
          this.containerResizeObserver.observe(this.containerFilters);
        }
      }
    } catch {}
  }

  destroyResizeObserver() {
    try {
      if (this.containerResizeObserver && this.containerParent) {
        this.containerResizeObserver.unobserve(this.containerParent);
      }

      if (this.containerResizeObserver && this.containerFilters) {
        this.containerResizeObserver.unobserve(this.containerFilters);
      }
    } catch {}
  }

  onSelectClick() {
    this.ref.close({ item: this.item, selectedRow: this.selectedRow });
  }

  onCancelClick() {
    this.ref.close(undefined);
  }

  onChangeCustId = (custid: string) => {
    this.custid = custid;
    this.itemService.itemLookupDialogSelectedCustomer$.next(this.custid);

    this.updateCustomerProductGroupList();
    this.updateCustomerRateGroupList();
  };

  onMatchTypeChange(field: string, matchType: string) {
    this.matchTypes[field] = matchType;
  }

  updateCustomerProductGroupList() {
    this.form.get('productGroup').setValue(null);
    this.itemService.requestCustomerProductGroupList(this.custid);
  }

  initializeCustomerProductGroupDropdownList(list: Record<string, DataList>) {
    this.customerProductGroupDropdownList$.next(
      this.listService.getDropdownList(list[this.custid ?? 'all'], {
        header1: this.translate('core.services.list.dropdown.header.code'),
        header2: this.translate('core.services.list.dropdown.header.description'),
        header3: this.translate('item.service.custid'),
        key3: 'custid',
      })
    );
  }

  updateCustomerRateGroupList() {
    this.form.get('rateGroup').setValue(null);
    this.itemService.requestCustomerRateGroupList(this.custid);
  }

  initializeCustomerRateGroupDropdownList(list: Record<string, DataList>) {
    this.customerRateGroupDropdownList$.next(
      this.listService.getDropdownList(list[this.custid ?? 'all'], {
        header1: this.translate('core.services.list.dropdown.header.code'),
        header2: this.translate('core.services.list.dropdown.header.description'),
        header3: this.translate('item.service.custid'),
        key3: 'custid',
      })
    );
  }

  applyFilters() {
    const filters: Filters = {};

    if (this.form.get('item').value) {
      filters.itemOrAlias = { value: this.form.get('item').value, matchMode: this.matchTypes.item ?? 'startsWith' };
    }

    if (this.form.get('productGroup').value) {
      filters.productGroup = { value: this.form.get('productGroup').value };
    }

    if (this.form.get('rateGroup').value) {
      filters.rateGroup = { value: this.form.get('rateGroup').value };
    }

    this.store.dispatch(GridActions.SetFilters({ id: this.gridId, filters }));
  }

  reset(): void {
    this.form.setValue({
      item: null,
      productGroup: null,
      rateGroup: null,
    });

    this.itemService.requestCustomerProductGroupList(null);
    this.itemService.requestCustomerRateGroupList(null);
  }
}
